import InviteModal from "@components/InviteModal";
import RecommendedPartneringRequests from "@components/RecommendedPartneringRequests";
import { RequestPageBanner } from "@components/RequestPageComponents";
import TRLs from "@components/TRLs";
import toast from "@components/Toast";
import { Button, Icon, PageContainer, Tag, TextLink, Tooltip } from "@components/library";
import RequestAttachmentsContainer from "@components/library/Attachments/RequestAttachmentsContainer";
import { COLORS, FEATURE_FLAGS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { useSearchParams } from "@hooks/router";
import { fetchCurrentUser, toggleLoginModal } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import { getRfpForRfpPage } from "@requests/rfps";
import { marketplacePath } from "@routes/utils";
import { useQuery } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { RfpStatus } from "@tsTypes/rfps";
import { getArrayFromRubyRange, track } from "@utils/appUtils";
import { setPrevUrlCookie, setSignupPrefillEmailCookie } from "@utils/authUtils";
import { filterByValidPartnershipModels, getRfpStatus } from "@utils/rfpUtils";
import { fixMarkdownNewlines, renderMarkdownText } from "@utils/textUtils";
import type { AxiosError } from "axios";
import { useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import styled from "styled-components";
import RequestDiscussion from "../../components/RequestDiscussion/RequestDiscussion";
import PreviewRfpPageBanner from "./PreviewRfpPageBanner";
import RfpCta from "./RfpCta";
// @ts-ignore
import RequestPageTagsContainer from "@components/RequestPageComponents/RequestPageTagsContainer";
import ContentClipFade from "@components/shared/ContentClipFade/ContentClipFade";
import { RfpPageRequest, RfpPageResponse } from "@tsTypes/index";
import { REQUEST_PARTNER_TYPE_LABELS } from "@tsTypes/requests";
import { UserRole } from "@tsTypes/users";
import { confidentialityAwareCompanyName } from "@utils/companyUtils";
import { t } from "@utils/i18n";
import RequestFAQs from "../../components/RequestFAQs";
import AboutCompany from "../../components/RequestPageComponents/AboutCompany";
import {
  Columns,
  Left,
  Right,
  Center as _Center,
} from "../RequestForStartupPage/RequestForStartupPage";
import { GateContainer, RightColumnFade, useProgressiveDisclosure } from "../shared/progressive";
import AccessCode from "./AccessCode";
import AccessCodeBanner from "./AccessCodeBanner";
import Benefits from "./Benefits";
import Sidebar, { Section as SidebarSection } from "./Sidebar";
import ProgressiveGate from "./progressive/ProgressiveGate";

interface Props {
  asPreview?: boolean;
  rfpPreviewData?: RfpPageRequest;
  isResponsive?: boolean;
}

export const ACCESS_CODE_PARAM = "access_code";

const RfpPage = ({ asPreview = false, rfpPreviewData, isResponsive = true }: Props) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);

  const [needsAccessCode, setNeedsAccessCode] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState("");
  const [accessCodeRequestData, setAccessCodeRequestData] = useState<{
    company_name: string;
    request_title: string;
  }>();
  const [enteredAccessCode, setEnteredAccessCode] = useState<string | null>(null);

  const rfpDiscussionRef = useRef<HTMLDivElement>(null);
  const gateTopRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const lookingForRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const whoWeAreRef = useRef<HTMLDivElement>(null);
  const qaRef = useRef<HTMLDivElement>(null);
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const dispatch = useDispatch();
  const { identifier, previewHash } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { fromHref } = location.state || {};
  const [searchParams] = useSearchParams();
  const accessCodeParam = searchParams.get(ACCESS_CODE_PARAM);

  const isFormPreview = asPreview && Boolean(rfpPreviewData);
  const isPreviewWithoutData = asPreview && !rfpPreviewData;
  const decodedIdentifier = isPreviewWithoutData && previewHash ? atob(previewHash) : undefined;

  const { data: queryData, isLoading: isQueryLoading } = useQuery<RfpPageResponse>(
    [
      "rfpPage",
      asPreview ? "preview" : "page",
      isPreviewWithoutData ? decodedIdentifier : identifier,
      enteredAccessCode ?? accessCodeParam ?? null,
      isFormPreview ? "formPreview" : null,
    ],
    async () => {
      if (isFormPreview) return Promise.reject(new Error("No fetch on form preview"));

      const slug = (isPreviewWithoutData ? decodedIdentifier : identifier)!;
      const code = isPreviewWithoutData ? previewHash : enteredAccessCode ?? accessCodeParam;
      const response = await getRfpForRfpPage(slug, code);
      return response;
    },
    {
      enabled: (!asPreview && Boolean(identifier)) || isPreviewWithoutData,
      retry: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      onSuccess: (res) => {
        const rfpRequest = res.request;
        if (!rfpRequest?.id) {
          history.push("/");
          return;
        }

        document.title = `${rfpRequest.title} | ${rfpRequest.requestable.rfp_program?.name} | Halo`;

        dispatch(fetchCurrentUser());

        track(SegmentEventName.VIEW, {
          request_id: rfpRequest.id,
          requestable: {
            requestable_id: rfpRequest.id,
            requestable_type: rfpRequest.requestable_type,
          },
          react_component: "RfpPage",
        });

        if (searchParams.get("saved")) {
          toast.success(
            <p>
              Saved! You can find your draft in&nbsp;
              <Link to="/my_proposals" style={{ textDecoration: "underline", color: "white" }}>
                My proposals
              </Link>
              .
            </p>
          );
        }

        if (enteredAccessCode) {
          searchParams.set(ACCESS_CODE_PARAM, enteredAccessCode);
          history.replace({ search: searchParams.toString() });
        }

        setNeedsAccessCode(false);
        setAccessCodeError("");
        setAccessCodeRequestData(undefined);
      },
      onError: (err) => {
        const e = err as AxiosError<any>;
        const status = e?.response?.status;

        if (status === 401) {
          if (needsAccessCode) {
            setAccessCodeError("Access code incorrect.");
          } else {
            setNeedsAccessCode(true);
            setAccessCodeRequestData(e.response?.data);
          }
          return;
        }

        history.push("/");
      },
    }
  );

  const request = rfpPreviewData ?? queryData?.request ?? ({} as RfpPageRequest);
  const rfp = request.requestable;
  const company = request.company;

  const isConfidential = request?.confidential || rfpPreviewData?.confidential;
  const { shouldGate, topOffset, paywallJsonLd } = useProgressiveDisclosure(request?.title);
  const gatedCompanyName = confidentialityAwareCompanyName(
    request?.company,
    Boolean(request?.confidential)
  );

  useLayoutEffect(() => {
    let animationFrameId: number;

    const checkAndScroll = () => {
      if (window.location.hash === "#qa" && rfpDiscussionRef.current) {
        rfpDiscussionRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        animationFrameId = requestAnimationFrame(checkAndScroll);
      }
    };

    checkAndScroll();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [rfpDiscussionRef]);

  if (needsAccessCode) {
    return (
      <AccessCode
        onSubmit={(code: string) => {
          setEnteredAccessCode(code);
        }}
        error={accessCodeError}
        requestData={accessCodeRequestData}
      />
    );
  }

  const isLoading = !isFormPreview && isQueryLoading;
  if (isLoading) {
    return <Loader inline="centered" />;
  }

  const showRfpDiscussionBanner =
    getRfpStatus(request) !== RfpStatus.IN_EXTENSION && rfp.is_discussion_enabled;
  const trlRange = rfp.trl_range ? getArrayFromRubyRange(rfp.trl_range) : null;

  const shouldShowAccessCodeBanner =
    !asPreview &&
    request.visibility.none &&
    request.access_code &&
    (currentUser.role === UserRole.SPONSOR || currentUser.is_admin);

  const hasBenefits = Boolean(
    (filterByValidPartnershipModels(rfp.partnership_models ?? []).length ?? 0) +
      (request.funding_benefits?.length ?? 0) +
      (request.non_monetary_benefits?.length ?? 0)
  );

  const detailsContent = (
    <>
      {Boolean(request.request_requirements?.length) && (
        <>
          <Subheading>{t("pages.rfp_page.headings.must_have_requirements")}</Subheading>
          <BulletedSection data-testid="rfp-must-have-requirements">
            {renderMarkdownText(
              request.request_requirements
                .map((requirement) => `* ${requirement.description}`)
                .join("\n")
            )}
          </BulletedSection>
        </>
      )}

      {(rfp.preferred_requirements?.length ?? 0) > 2 && (
        <>
          <Subheading>{t("pages.rfp_page.headings.preferred_requirements")}</Subheading>
          <BulletedSection data-testid="rfp-nice-to-have-requirements">
            {renderMarkdownText(rfp.preferred_requirements)}
          </BulletedSection>
        </>
      )}
      {(rfp.out_of_scope?.length ?? 0) > 2 && (
        <>
          <Subheading>{t("pages.rfp_page.headings.out_of_scope")}</Subheading>
          <BulletedSection data-testid="rfp-out-of-scope">
            {renderMarkdownText(rfp.out_of_scope)}
          </BulletedSection>
        </>
      )}
      {trlRange ? (
        <TRLsContainer>
          <Subheading>
            {t("pages.rfp_page.headings.trl")}
            <Tag
              content={
                <TrlTagContent>
                  {trlRange[0] === trlRange[1]
                    ? `Level ${trlRange[0]}`
                    : `Levels ${trlRange[0]}-${trlRange[1]}`}
                  <Tooltip
                    content="Technology readiness levels help describe the maturity of solutions. An organization may seek solutions that fall only within a specified range of TRLs."
                    tooltipWidth="410px"
                  >
                    <Icon name="Info" size="xs" color={COLORS.NEUTRAL_400} />
                  </Tooltip>
                </TrlTagContent>
              }
              size="md"
              margin="0 0 0 16px"
            />
          </Subheading>
          <TRLs trlRange={trlRange} />
        </TRLsContainer>
      ) : null}
      <AnchorSection ref={benefitsRef} topOffset={topOffset}>
        <Benefits
          partnershipModels={rfp.partnership_models ?? []}
          fundingBenefits={request.funding_benefits}
          nonMonetaryBenefits={request.non_monetary_benefits}
          isResponsive={isResponsive}
        />
      </AnchorSection>
      <AnchorSection ref={whoWeAreRef} topOffset={topOffset}>
        {!isConfidential && <AboutCompany request={request} isResponsive={isResponsive} />}
      </AnchorSection>
      {rfp.is_discussion_enabled && (
        <div>
          <AnchorSection ref={qaRef} topOffset={topOffset} />
          <RequestDiscussion
            request={request}
            ref={rfpDiscussionRef}
            isCommenting={isCommenting}
            setIsCommenting={setIsCommenting}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      {paywallJsonLd && <script type="application/ld+json">{JSON.stringify(paywallJsonLd)}</script>}
      <InviteModal
        open={isShareModalOpen}
        requestId={request.id}
        closeModal={() => setIsShareModalOpen(false)}
        trackAndToastHandler={() => toast.success("Partnering request shared!")}
        inviteMessage=""
        inviteUrl="/notifications/share_rfp"
        modalHeader={`Share this partnering request with ${
          currentUser.role === 2 ? "faculty" : "colleagues"
        }`}
        modalSubheader={`Your ${
          currentUser.role === 2 ? "faculty" : "colleague(s)"
        } will receive an email with a link to this partnering request`}
        inviteSubject={`${currentUser.first_name} ${currentUser.last_name} shared a partnering request with you.`}
        typeOfInvite="shareRFP"
      />
      {asPreview && !rfpPreviewData && <PreviewRfpPageBanner />}
      {shouldShowAccessCodeBanner && <AccessCodeBanner accessCode={request.access_code!} />}
      <Container asPreview={asPreview} isResponsive={isResponsive} data-testid="rfp-page-main">
        <PageContainer hasPaddingTop={asPreview} hasTopDivider>
          {!asPreview && (
            <TextLink
              to={fromHref ?? marketplacePath("requests")}
              font={FONTS.MEDIUM_2}
              margin="24px 0"
            >
              <Icon name="Chevron Left" margin="0 0 0 -8px" />
              All partnering requests
            </TextLink>
          )}
          <RequestPageBanner request={request} asPreview={asPreview} />
          <MainContent isResponsive={isResponsive}>
            <Columns>
              <Left>
                <Sidebar
                  backgroundRef={backgroundRef}
                  lookingForRef={lookingForRef}
                  benefitsRef={benefitsRef}
                  whoWeAreRef={whoWeAreRef}
                  qaRef={qaRef}
                  onSignupClick={() => {
                    setSignupPrefillEmailCookie("");
                    setPrevUrlCookie();
                    dispatch(toggleLoginModal({ open: true, initialView: "signup" }));
                  }}
                  isConfidential={isConfidential}
                  topOffset={topOffset}
                  hasBenefits={hasBenefits}
                  isDiscussionEnabled={Boolean(rfp.is_discussion_enabled)}
                  blockedKeys={
                    shouldGate
                      ? [SidebarSection.BENEFITS, SidebarSection.WHO_WE_ARE, SidebarSection.QA]
                      : []
                  }
                />
              </Left>
              <Center>
                {showRfpDiscussionBanner && (
                  <QuestionCTA isResponsive={isResponsive}>
                    <div>
                      <Heading>Have questions about this request?</Heading>
                      <div>
                        Get them answered by the team at{" "}
                        {!isConfidential && "company_name" in company
                          ? company.company_name
                          : "the company"}
                        .
                      </div>
                    </div>
                    <Button
                      width="max-content"
                      onClick={() => {
                        if (shouldGate)
                          return gateTopRef.current?.scrollIntoView({ behavior: "smooth" });

                        rfpDiscussionRef.current?.scrollIntoView({ behavior: "smooth" });
                        if (rfp.is_discussion_enabled && currentUser.role === 0) {
                          setIsCommenting(true);
                        }
                      }}
                    >
                      Ask your question
                    </Button>
                  </QuestionCTA>
                )}
                <Heading ref={backgroundRef} topOffset={topOffset}>
                  {t("pages.rfp_page.headings.background")}
                </Heading>
                <div data-testid="rfp-background">
                  {renderMarkdownText(fixMarkdownNewlines(rfp.background))}
                </div>
                <Heading ref={lookingForRef} topOffset={topOffset}>
                  {t("pages.rfp_page.headings.looking_for")}
                </Heading>
                <BulletedSection data-testid="rfp-looking-for">
                  {renderMarkdownText(rfp.looking_for)}
                </BulletedSection>
                <RequestAttachmentsContainer
                  heading={<Subheading>{t("pages.rfp_page.headings.attachments")}</Subheading>}
                  attachments={request.request_attachments}
                />
                {/* These fields are stored as strings, and the first 3 characters just render a bullet point */}
                {Boolean(request.request_solutions?.length) && (
                  <>
                    <Subheading>{t("pages.rfp_page.headings.solutions_of_interest")}</Subheading>
                    <BulletedSection data-testid="rfp-solutions-of-interest">
                      {renderMarkdownText(
                        request.request_solutions.map((solution) => `* ${solution.name}`).join("\n")
                      )}
                    </BulletedSection>
                  </>
                )}
                {shouldGate ? (
                  <div>
                    <ContentClipFade
                      maxHeightPx={90}
                      className="paywalled-content"
                      aria-hidden={true}
                    >
                      {detailsContent}
                    </ContentClipFade>
                    <GateContainer ref={gateTopRef} className="progressive-gate-container">
                      <ProgressiveGate companyName={gatedCompanyName} />
                    </GateContainer>
                  </div>
                ) : (
                  detailsContent
                )}
              </Center>
              <RightColumnFade
                shouldGate={shouldGate}
                gateRef={gateTopRef}
                as={RightColumnMobile as any}
              >
                <RfpCta
                  request={request}
                  asPreview={asPreview}
                  setIsShareModalOpen={setIsShareModalOpen}
                />
                <RequestPageTagsContainer
                  tags={request.partner_types?.map((type) => REQUEST_PARTNER_TYPE_LABELS[type])}
                  heading="Eligible partners"
                />
                <RequestPageTagsContainer
                  tags={request.areas_of_expertise}
                  heading="Seeking partners focused on"
                />
                {request.visibility.any && <RequestFAQs request={request} />}
              </RightColumnFade>
            </Columns>
          </MainContent>
          {Boolean(FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS && !asPreview) && (
            <RecommendedPartneringRequests requestId={request.id} />
          )}
        </PageContainer>
      </Container>
    </>
  );
};

export default RfpPage;

const Container = styled.div`
  pointer-events: ${({ asPreview }) => (asPreview ? "none" : "auto")};
  ${({ isResponsive }) => !isResponsive && `width: ${WINDOW_DIMENSIONS.BASE_WINDOW_WIDTH}px;`}
`;
const QuestionCTA = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 64px;
  background: ${COLORS.BLUE_LIGHT_250};
  padding: 32px 64px 32px 48px;
  border-radius: 12px;
  ${FONTS.REGULAR_1}
  ${({ isResponsive }) =>
    isResponsive &&
    `
      ${WINDOW_DIMENSIONS.DESKTOP_MEDIA_QUERY} {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
      }
  `}
`;
const MainContent = styled.div`
  gap: 190px;
  padding: 84px 0 100px;
  & p {
    ${FONTS.REGULAR_1};
    margin: 0;
  }
  ${({ isResponsive }) =>
    isResponsive &&
    `
      ${WINDOW_DIMENSIONS.DESKTOP_MEDIA_QUERY} {
        gap: 80px;
      }
      ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
        display: flex;
        flex-direction: column-reverse;
        gap: 64px;
      }
      ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
        padding: 84px 32px 100px;
      }
  `}
`;

const Heading = styled.div<{ topOffset?: number }>`
  ${FONTS.HEADING_4_SEMIBOLD};
  margin-bottom: 24px;
  scroll-margin-top: ${({ topOffset = 0 }) => `${topOffset}px`};
  &:not(:first-of-type) {
    margin-top: 64px;
  }
`;
const Subheading = styled.div`
  display: flex;
  align-items: center;
  ${FONTS.SEMIBOLD_1};
  margin: 24px 0 8px;
`;
const BulletedSection = styled.div`
  & ul {
    padding-left: 4px;
  }
`;
// Used in RequestForStartupPage
export const TRLsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
`;
export const TrlTagContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Center = styled(_Center)`
  display: block;
  gap: 0px;
`;

// Keep right column content order on mobile/tablet; do not reverse
const RightColumnMobile = styled(Right as any)`
  ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

// Anchors for sections that are not using the Heading component
const AnchorSection = styled.div<{ topOffset?: number }>`
  scroll-margin-top: ${({ topOffset = 0 }) => `${topOffset}px`};
`;
